import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import { pageview } from "../analytics";

/**
 * Admin surfaces, by path prefix — the routes `AdminGuard` wraps in `app.tsx`.
 *
 * Staff use the AG Grid maintenance screens heavily, and every one of those visits used to land in
 * the same reports as customer traffic. Tagged rather than dropped: a dimension can be filtered out
 * in a report and put back, whereas a page view never sent is gone for good.
 */
const ADMIN_PATH_PREFIXES = [
  "/item-maintenance",
  "/banner-maintenance",
  "/education-item-maintenance",
  "/delivery-maintenance",
  "/alegra-items",
  "/contabilidad",
];

export const resolvePageArea = (pathname: string): "admin" | "storefront" =>
  ADMIN_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    ? "admin"
    : "storefront";

/**
 * One `page_view` per page, where "page" means the path (EB-558).
 *
 * This used to depend on the whole `location` object and report
 * `pathname + search + hash`. `HeaderSearch` writes the search term into `?q=` on a debounce, so
 * every pause in typing counted as another visit to `/` — refining a search manufactured several
 * phantom landings on the home page. Depending on `pathname` alone is the entire fix: a search is
 * already reported by the `search` event, and it is not a navigation.
 *
 * Held until Auth0 resolves (EB-558) so the first `page_view` of a session already carries the
 * `login_state` user property `useAnalyticsUser` sets. Costs nothing in coverage: `App` renders a
 * `PageLoader` until exactly the same moment, so there is no page on screen to report before it —
 * and a gate that never opens means an app stuck on that loader, which is broken with or without
 * the page view.
 */
const usePageTracking = () => {
  const { pathname } = useLocation();
  const { isLoading } = useAuth0();

  useEffect(() => {
    if (isLoading) return;
    const path = pathname || "/";
    pageview(path, { page_area: resolvePageArea(path) });
  }, [pathname, isLoading]);
};

export default usePageTracking;
